import React, { useEffect, useState } from 'react';
import { Users, UserPlus, Mail, Trash2, Shield, Edit3 } from 'lucide-react';
import { practiceApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

interface Practice {
  id: string;
  name: string;
  description?: string;
}

interface Member {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  permission_create_clients: boolean;
  permission_manage_members: boolean;
  permission_view_reports: boolean;
  permission_manage_invoices: boolean;
  is_owner: boolean;
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  created_at: string;
}

const ROLE_LABELS: Record<string, string> = {
  owner: 'Inhaber',
  admin: 'Admin',
  coach: 'Coach',
  assistenz: 'Assistenz',
};

const ROLE_COLORS: Record<string, { bg: string; color: string }> = {
  owner: { bg: 'rgba(91, 124, 168, 0.12)', color: 'var(--info)' },
  admin: { bg: 'rgba(192, 86, 79, 0.10)', color: 'var(--danger)' },
  coach: { bg: 'rgba(91, 140, 90, 0.10)', color: 'var(--success)' },
  assistenz: { bg: 'rgba(160, 120, 60, 0.10)', color: '#a0783c' },
};

const ROLE_DEFAULTS: Record<string, { permission_create_clients: boolean; permission_manage_members: boolean; permission_view_reports: boolean; permission_manage_invoices: boolean }> = {
  admin: { permission_create_clients: true, permission_manage_members: true, permission_view_reports: true, permission_manage_invoices: true },
  coach: { permission_create_clients: true, permission_manage_members: false, permission_view_reports: true, permission_manage_invoices: true },
  assistenz: { permission_create_clients: true, permission_manage_members: false, permission_view_reports: false, permission_manage_invoices: false },
};

function RoleBadge({ role }: { role: string }) {
  const style = ROLE_COLORS[role] || { bg: 'var(--bg)', color: 'var(--text-muted)' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 10px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 600,
      background: style.bg, color: style.color,
    }}>
      <Shield size={11} />
      {ROLE_LABELS[role] || role}
    </span>
  );
}

export default function TeamManagement() {
  const { showToast } = useToast();
  const { subscription } = useAuth();
  const maxMembers = subscription?.max_users ?? null;

  const [practice, setPractice] = useState<Practice | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);

  // Create form
  const [createName, setCreateName] = useState('');
  const [creating, setCreating] = useState(false);

  // Edit practice info
  const [editInfo, setEditInfo] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [savingInfo, setSavingInfo] = useState(false);

  // Invite form
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'coach' | 'assistenz'>('coach');
  const [invitePerms, setInvitePerms] = useState(ROLE_DEFAULTS['coach']);
  const [inviting, setInviting] = useState(false);

  // Member edit
  const [editingMember, setEditingMember] = useState<string | null>(null);
  const [memberEdits, setMemberEdits] = useState<Record<string, Partial<Member>>>({});

  const load = async () => {
    try {
      const res = await practiceApi.me();
      setPractice(res.data);
      const [membersRes, invRes] = await Promise.all([
        practiceApi.members(),
        practiceApi.invitations(),
      ]);
      setMembers(membersRes.data || []);
      setInvitations(invRes.data || []);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setPractice(null);
      } else {
        showToast('Fehler beim Laden', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createName.trim()) return;
    setCreating(true);
    try {
      await practiceApi.create({ name: createName.trim() });
      showToast('Praxis erstellt', 'success');
      setCreateName('');
      await load();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Erstellen', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleSaveInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingInfo(true);
    try {
      await practiceApi.update({ name: editName, description: editDesc || undefined });
      showToast('Praxis-Info gespeichert', 'success');
      setEditInfo(false);
      await load();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Speichern', 'error');
    } finally {
      setSavingInfo(false);
    }
  };

  const handleRoleChange = (role: 'admin' | 'coach' | 'assistenz') => {
    setInviteRole(role);
    setInvitePerms(ROLE_DEFAULTS[role]);
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await practiceApi.invite({
        email: inviteEmail.trim(),
        role: inviteRole,
        ...invitePerms,
      });
      showToast('Einladung gesendet', 'success');
      setInviteEmail('');
      setInviteRole('coach');
      setInvitePerms(ROLE_DEFAULTS['coach']);
      setShowInviteForm(false);
      await load();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Einladen', 'error');
    } finally {
      setInviting(false);
    }
  };

  const handleCancelInvitation = async (id: string) => {
    try {
      await practiceApi.cancelInvitation(id);
      showToast('Einladung zurückgezogen', 'success');
      await load();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler', 'error');
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!window.confirm('Mitglied wirklich entfernen?')) return;
    try {
      await practiceApi.removeMember(userId);
      showToast('Mitglied entfernt', 'success');
      await load();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler', 'error');
    }
  };

  const startEditMember = (member: Member) => {
    setEditingMember(member.user_id);
    setMemberEdits((prev) => ({
      ...prev,
      [member.user_id]: {
        role: member.role,
        permission_create_clients: member.permission_create_clients,
        permission_manage_members: member.permission_manage_members,
        permission_view_reports: member.permission_view_reports,
        permission_manage_invoices: member.permission_manage_invoices,
      },
    }));
  };

  const handleSaveMember = async (userId: string) => {
    const data = memberEdits[userId];
    if (!data) return;
    try {
      await practiceApi.updateMember(userId, data);
      showToast('Mitglied aktualisiert', 'success');
      setEditingMember(null);
      await load();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler', 'error');
    }
  };

  if (loading) {
    return (
      <div className="card" style={{ maxWidth: 760, padding: 32 }}>
        <p className="text-muted text-sm">Wird geladen...</p>
      </div>
    );
  }

  // No practice yet — show create form
  if (!practice) {
    return (
      <div className="card" style={{ maxWidth: 520, padding: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <Users size={22} style={{ color: 'var(--primary)' }} />
          <h3 style={{ margin: 0 }}>Praxis erstellen</h3>
        </div>
        <p className="text-muted text-sm" style={{ marginBottom: 24 }}>
          Erstellen Sie eine Praxis, um Kollegen einzuladen und gemeinsam mit Klienten zu arbeiten.
        </p>
        <form onSubmit={handleCreate}>
          <div className="form-group">
            <label>Praxisname</label>
            <input
              type="text"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              placeholder="z.B. Coaching-Praxis Mustermann"
              required
              autoFocus
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={creating || !createName.trim()}>
            <Users size={16} /> {creating ? 'Erstellen...' : 'Praxis erstellen'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 760, display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Praxis-Info */}
      <div className="card" style={{ padding: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>Praxis-Info</h3>
          {!editInfo && (
            <button
              className="btn btn-secondary"
              style={{ padding: '6px 14px', fontSize: '0.85rem' }}
              onClick={() => { setEditName(practice.name); setEditDesc(practice.description || ''); setEditInfo(true); }}
            >
              <Edit3 size={14} /> Bearbeiten
            </button>
          )}
        </div>

        {editInfo ? (
          <form onSubmit={handleSaveInfo}>
            <div className="form-group">
              <label>Praxisname</label>
              <input value={editName} onChange={(e) => setEditName(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Beschreibung (optional)</label>
              <textarea value={editDesc} onChange={(e) => setEditDesc(e.target.value)} rows={2} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="submit" className="btn btn-primary" disabled={savingInfo}>
                {savingInfo ? 'Speichern...' : 'Speichern'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setEditInfo(false)}>Abbrechen</button>
            </div>
          </form>
        ) : (
          <div>
            <p style={{ fontWeight: 600, marginBottom: 4 }}>{practice.name}</p>
            {practice.description && <p className="text-muted text-sm" style={{ margin: 0 }}>{practice.description}</p>}
          </div>
        )}
      </div>

      {/* Mitglieder */}
      <div className="card" style={{ padding: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h3 style={{ margin: 0 }}>
              <Users size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
              Mitglieder
            </h3>
            {maxMembers != null && (
              <span style={{
                display: 'inline-flex', alignItems: 'center',
                padding: '2px 10px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 600,
                background: members.length >= maxMembers ? 'rgba(192, 86, 79, 0.10)' : 'rgba(91, 140, 90, 0.10)',
                color: members.length >= maxMembers ? 'var(--danger)' : 'var(--success)',
              }}>
                {members.length} / {maxMembers} Mitglieder
              </span>
            )}
          </div>
          <button
            className="btn btn-primary"
            style={{ padding: '6px 14px', fontSize: '0.85rem' }}
            onClick={() => setShowInviteForm(!showInviteForm)}
          >
            <UserPlus size={14} /> Einladen
          </button>
        </div>

        {/* Invite Form */}
        {showInviteForm && (
          <div style={{
            padding: 20, marginBottom: 20,
            background: 'var(--bg)', borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
          }}>
            <h4 style={{ marginBottom: 16 }}>Neues Mitglied einladen</h4>
            <form onSubmit={handleInvite}>
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label>E-Mail-Adresse</label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="kollegin@beispiel.de"
                    required
                    autoFocus
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Rolle</label>
                  <select
                    value={inviteRole}
                    onChange={(e) => handleRoleChange(e.target.value as 'admin' | 'coach' | 'assistenz')}
                  >
                    <option value="admin">Admin</option>
                    <option value="coach">Coach</option>
                    <option value="assistenz">Assistenz</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: '0.88rem' }}>Berechtigungen</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                  {[
                    { key: 'permission_create_clients', label: 'Klienten anlegen' },
                    { key: 'permission_manage_members', label: 'Mitglieder verwalten' },
                    { key: 'permission_view_reports', label: 'Berichte einsehen' },
                    { key: 'permission_manage_invoices', label: 'Rechnungen verwalten' },
                  ].map(({ key, label }) => (
                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.88rem' }}>
                      <input
                        type="checkbox"
                        checked={invitePerms[key as keyof typeof invitePerms]}
                        onChange={(e) => setInvitePerms({ ...invitePerms, [key]: e.target.checked })}
                        style={{ width: 16, height: 16 }}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary" disabled={inviting || !inviteEmail.trim()}>
                  <Mail size={14} /> {inviting ? 'Wird gesendet...' : 'Einladung senden'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowInviteForm(false)}>Abbrechen</button>
              </div>
            </form>
          </div>
        )}

        {/* Member List */}
        {members.length === 0 ? (
          <p className="text-muted text-sm">Noch keine Mitglieder.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {members.map((member) => {
              const isEditing = editingMember === member.user_id;
              const edits = memberEdits[member.user_id] || {};

              return (
                <div key={member.user_id} style={{
                  padding: '14px 16px', borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)', background: 'var(--bg-card)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                        <span style={{ fontWeight: 600 }}>{member.first_name} {member.last_name}</span>
                        {isEditing ? (
                          <select
                            value={edits.role || member.role}
                            onChange={(e) => setMemberEdits((prev) => ({ ...prev, [member.user_id]: { ...prev[member.user_id], role: e.target.value } }))}
                            style={{ fontSize: '0.82rem', padding: '2px 8px', height: 28 }}
                            disabled={member.is_owner}
                          >
                            <option value="admin">Admin</option>
                            <option value="coach">Coach</option>
                            <option value="assistenz">Assistenz</option>
                          </select>
                        ) : (
                          <RoleBadge role={member.role} />
                        )}
                      </div>
                      <p className="text-muted text-sm" style={{ margin: 0 }}>{member.email}</p>

                      {isEditing && (
                        <div style={{ marginTop: 12 }}>
                          <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: '0.82rem' }}>Berechtigungen</label>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
                            {[
                              { key: 'permission_create_clients', label: 'Klienten anlegen' },
                              { key: 'permission_manage_members', label: 'Mitglieder verwalten' },
                              { key: 'permission_view_reports', label: 'Berichte einsehen' },
                              { key: 'permission_manage_invoices', label: 'Rechnungen verwalten' },
                            ].map(({ key, label }) => (
                              <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.82rem' }}>
                                <input
                                  type="checkbox"
                                  checked={edits[key as keyof Member] as boolean ?? member[key as keyof Member] as boolean}
                                  onChange={(e) => setMemberEdits((prev) => ({ ...prev, [member.user_id]: { ...prev[member.user_id], [key]: e.target.checked } }))}
                                  style={{ width: 15, height: 15 }}
                                />
                                {label}
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      {!member.is_owner && (
                        <>
                          {isEditing ? (
                            <>
                              <button
                                className="btn btn-primary"
                                style={{ padding: '5px 12px', fontSize: '0.82rem' }}
                                onClick={() => handleSaveMember(member.user_id)}
                              >
                                Speichern
                              </button>
                              <button
                                className="btn btn-secondary"
                                style={{ padding: '5px 12px', fontSize: '0.82rem' }}
                                onClick={() => setEditingMember(null)}
                              >
                                Abbrechen
                              </button>
                            </>
                          ) : (
                            <button
                              className="btn btn-secondary"
                              style={{ padding: '5px 10px' }}
                              title="Bearbeiten"
                              onClick={() => startEditMember(member)}
                            >
                              <Edit3 size={14} />
                            </button>
                          )}
                          <button
                            className="btn btn-danger"
                            style={{ padding: '5px 10px' }}
                            title="Entfernen"
                            onClick={() => handleRemoveMember(member.user_id)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Offene Einladungen */}
      {invitations.length > 0 && (
        <div className="card" style={{ padding: 28 }}>
          <h3 style={{ marginBottom: 16 }}>
            <Mail size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Offene Einladungen
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {invitations.map((inv) => (
              <div key={inv.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px', borderRadius: 'var(--radius-sm)',
                border: '1px dashed var(--border)', background: 'var(--bg)',
              }}>
                <div>
                  <span style={{ fontWeight: 500 }}>{inv.email}</span>
                  <span style={{ marginLeft: 10 }}>
                    <RoleBadge role={inv.role} />
                  </span>
                  <p className="text-muted text-sm" style={{ margin: '2px 0 0' }}>
                    Gesendet am {new Date(inv.created_at).toLocaleDateString('de-DE')}
                  </p>
                </div>
                <button
                  className="btn btn-secondary"
                  style={{ padding: '5px 12px', fontSize: '0.82rem' }}
                  onClick={() => handleCancelInvitation(inv.id)}
                >
                  <Trash2 size={13} /> Zurückziehen
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
